import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Grid,
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
} from '@material-ui/core';
import {
  Progress,
  Page,
  Header,
  Content,
  HeaderLabel,
  InfoCard,
} from '@backstage/core-components';
import StorageIcon from '@material-ui/icons/Storage';
import BackupIcon from '@material-ui/icons/Backup';
import SettingsIcon from '@material-ui/icons/Settings';
import AddIcon from '@material-ui/icons/Add';
import { databaseService } from '../../services/databaseService';
import { backupService } from '../../services/backupService';
import { storageService } from '../../services/storageService';
import { monitoringService } from '../../services/monitoringService';
import { systemService } from '../../services/systemService';
import {
  DatabaseCluster,
  Backup,
  StorageLocation,
  MonitoringEndpoint,
} from '../../types';
import {
  DatabaseListView,
  CreateDatabaseDialog,
  BackupsView,
  SettingsView,
} from './components';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`openeverest-tabpanel-${index}`}
      aria-labelledby={`openeverest-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

export const OpenEverestPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [clusters, setClusters] = useState<DatabaseCluster[]>([]);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>(
    [],
  );
  const [monitoringEndpoints, setMonitoringEndpoints] = useState<
    MonitoringEndpoint[]
  >([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [systemConfig, setSystemConfig] = useState<any>(null);

  // Tracks whether the component is still mounted so async handlers can
  // avoid calling setState (via loadData) after unmount.
  const cancelledRef = useRef(false);

  useEffect(() => {
    loadData();

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Statistics derived from clusters/backups so they never reflect a
  // transient state where only one of the two has been updated.
  const stats = useMemo(() => {
    const totalClusters = clusters.length;
    const runningClusters = clusters.filter(
      c => c.status?.status === 'ready',
    ).length;
    const totalBackups = backups.length;
    const totalStorageBytes = clusters.reduce((sum, c) => {
      const storage = c.status?.activeStorage || '0Gi';
      const match = storage.match(/(\d+)Gi/);
      return sum + (match ? parseInt(match[1], 10) : 0);
    }, 0);

    return {
      totalClusters,
      runningClusters,
      totalBackups,
      totalStorage: `${totalStorageBytes}Gi`,
    };
  }, [clusters, backups]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        clustersData,
        backupsData,
        storageData,
        monitoringData,
        configData,
      ] = await Promise.all([
        databaseService.getClusters(),
        backupService.getBackups(),
        storageService.getStorageLocations(),
        monitoringService.getMonitoringEndpoints(),
        systemService.getSystemConfig(),
      ]);

      if (cancelledRef.current) return;

      setClusters(clustersData);
      setBackups(backupsData);
      setStorageLocations(storageData);
      setMonitoringEndpoints(monitoringData);
      setSystemConfig(configData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateDatabase = () => {
    setCreateDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCreateDialogOpen(false);
    loadData();
  };

  const handleDeleteCluster = async (namespace: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete database cluster ${name}?`,
      )
    ) {
      try {
        await databaseService.deleteCluster(namespace, name);
        if (cancelledRef.current) return;
        loadData();
      } catch (error) {
        console.error('Error deleting cluster:', error);
      }
    }
  };

  const handlePauseCluster = async (namespace: string, name: string) => {
    try {
      await databaseService.pauseCluster(namespace, name);
      if (cancelledRef.current) return;
      loadData();
    } catch (error) {
      console.error('Error pausing cluster:', error);
    }
  };

  const handleResumeCluster = async (namespace: string, name: string) => {
    try {
      await databaseService.resumeCluster(namespace, name);
      if (cancelledRef.current) return;
      loadData();
    } catch (error) {
      console.error('Error resuming cluster:', error);
    }
  };

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="OpenEverest" subtitle="Cloud-native database platform">
          <HeaderLabel
            label="Version"
            value={systemConfig?.version || '1.2.0'}
          />
        </Header>
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="OpenEverest"
        subtitle="Cloud-native database platform for PostgreSQL, MySQL, and MongoDB"
      >
        <HeaderLabel label="Version" value={systemConfig?.version || '1.2.0'} />
        <HeaderLabel
          label="K8s"
          value={systemConfig?.k8sVersion || 'Unknown'}
        />
      </Header>
      <Content>
        {/* Statistics Cards */}
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard title="Total Databases">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100px"
              >
                <Typography variant="h3">{stats.totalClusters}</Typography>
              </Box>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard title="Running">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100px"
              >
                <Typography variant="h3" style={{ color: '#4caf50' }}>
                  {stats.runningClusters}
                </Typography>
              </Box>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard title="Total Backups">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100px"
              >
                <Typography variant="h3">{stats.totalBackups}</Typography>
              </Box>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard title="Total Storage">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100px"
              >
                <Typography variant="h3">{stats.totalStorage}</Typography>
              </Box>
            </InfoCard>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="openeverest tabs"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab icon={<StorageIcon />} label="Databases" />
              <Tab icon={<BackupIcon />} label="Backups" />
              <Tab icon={<SettingsIcon />} label="Settings" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Database Clusters</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateDatabase}
              >
                Create Database
              </Button>
            </Box>
            <DatabaseListView
              clusters={clusters}
              onDelete={handleDeleteCluster}
              onPause={handlePauseCluster}
              onResume={handleResumeCluster}
              onRefresh={loadData}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <BackupsView
              backups={backups}
              clusters={clusters}
              storageLocations={storageLocations}
              onRefresh={loadData}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <SettingsView
              storageLocations={storageLocations}
              monitoringEndpoints={monitoringEndpoints}
              systemConfig={systemConfig}
              onRefresh={loadData}
            />
          </TabPanel>
        </Card>

        {/* Create Database Dialog */}
        <CreateDatabaseDialog
          open={createDialogOpen}
          onClose={handleDialogClose}
          storageLocations={storageLocations}
          monitoringEndpoints={monitoringEndpoints}
        />
      </Content>
    </Page>
  );
};
